import { useEffect, useMemo, useContext } from "react";
import { getModuleErrors } from "../functions/quoteFunctions";
import QuoteReviewWarningsTable from "./quoteReviewWarningsTable";
import { QuoteDataContext } from "../contexts/quoteDataContext";

const QuoteReviewWarningsForModules = ({
    warnings,
    quoteModuleGuid,
    quoteModules,
    onAddWarning,
    onRemoveWarning,
    onAcknowledgedWarningChange,
    onApprovedWarningChange
}) => {

    const quoteDataContext = useContext(QuoteDataContext);
    const allModules = quoteDataContext.productModules || [];
    const quoteModule = quoteModules.find(x => x.guid === quoteModuleGuid);
    const module = quoteDataContext.productModules.find(x => x.id === quoteModule?.moduleId);
    const userInputs = quoteModule.inputValues || [];
    const quoteGroupDescription = userInputs.find(input => input.inputGuid === "quoteGroupDescription")?.value;

    const validationErrors = useMemo(() =>  {
        // GetModuleErrors returns an array of errors with these properties:
        // { type, error, detail, inputGuid, moduleId }

        const errors = getModuleErrors(quoteModuleGuid, quoteModules, allModules) || []
        return errors.map(error => ({
            ...error,
            id: `${error.type}|${error.id}|${error.inputGuid || error.moduleId}|`
        }));
    }

    , [quoteModule, quoteModules, allModules, quoteModuleGuid]);

    const uniqueModuleErrors = useMemo(() =>
        Array.from(
            new Map(
                validationErrors.map(error => [error.error, error])
            ).values()
        )
    , [validationErrors]);

    const moduleWarnings = useMemo(() => {
        return warnings.filter(x => x.sectionId === "module|" + quoteModuleGuid);    
    }, [warnings]);

    const moduleWarningsTableData = useMemo(() => {
        const tableData = [];
        const acknowledgedWarnings = moduleWarnings.filter(x => x.acknowledged);
        const approvedWarnings = moduleWarnings.filter(x => x.approved);
        const acknowledgedMap = new Map(acknowledgedWarnings.map(w => [w.id, w]));
        const approvedMap = new Map(approvedWarnings.map(w => [w.id, w]));
        const moduleWarningIdsSet = new Set(moduleWarnings.map(w => w.id));

        uniqueModuleErrors.forEach(uniqueError => {
            const entriesForThisError = validationErrors.filter(validationError =>
                uniqueError.error === validationError.error
            );

            tableData.push({           
                id: uniqueError.error,
                warning: uniqueError.error,
                items: entriesForThisError.map(entry => {                    
                    const acknowledgedWarning = acknowledgedMap.get(entry.id);
                    const approvedWarning = approvedMap.get(entry.id);
                    const linkedModuleError = validationErrors.find(e => e.id === entry.id);

                    return ({
                        ...entry,
                        id: entry.id,
                        acknowledged: moduleWarningIdsSet.has(entry.id)
                          ? !!acknowledgedWarning // It's in the DB, so if it's in the acknowledged map, then true, else false
                          : (linkedModuleError?.acknowledgedBy ? true : false), /* If warning is not found in DB, then just default to true if it was a warning acknowledged when adding the module.  Otherwise false */
                        acknowledgedBy: moduleWarningIdsSet.has(entry.id)
                          ? acknowledgedWarning?.acknowledgedBy // Undefined if not acknowledged
                          : linkedModuleError?.acknowledgedBy, /* If it's not found in our map, then set to person that acknowledged it when adding the module (if any) */
                        approved: !!approvedWarning,
                        approvedBy: approvedWarning?.approvedBy,
                        detail: entry.detail
                    });
                })
            });
        });

        return tableData;
    }, [moduleWarnings, uniqueModuleErrors, validationErrors]);


    useEffect(() => {

        // Now we have a list of what warnings there currently are, we need to add any that aren't known to the quoteRevision, so 
        // we can store when they are acknowledged/approved
        moduleWarningsTableData.forEach(warningGroupInTable => {
            warningGroupInTable.items.forEach(warningItemInTable => {
                if (!moduleWarnings.some(w => w.id === warningItemInTable.id)) {
                    onAddWarning({
                        id: warningItemInTable.id,
                        sectionId: "module|" + quoteModuleGuid,
                        acknowledged: warningItemInTable.acknowledgedBy ? true : false,
                        acknowledgedBy: warningItemInTable.acknowledgedBy ? warningItemInTable.acknowledgedBy : null,
                        approved: false,
                        approvedBy: null
                    });
                }
            })
        })

        /*
        * Remove any warnings that are no longer needed from the quoteRevision
        */

        const noLongerNeededWarnings = moduleWarnings.filter(warning => 
            !validationErrors.find(i => i.id === warning.id)
        );

        noLongerNeededWarnings.forEach(warningToRemove => {
            console.log("Removing no longer needed warning", warningToRemove);
            onRemoveWarning(warningToRemove);
        });

    }, [moduleWarningsTableData, moduleWarnings, onAddWarning, onRemoveWarning]); 

    return (
        <QuoteReviewWarningsTable
            header={quoteGroupDescription + " Warnings"}
            warnings={moduleWarningsTableData}
            onAcknowledgedWarningChange={onAcknowledgedWarningChange}
            onApprovedWarningChange={onApprovedWarningChange}
        />    
    );
}
export default QuoteReviewWarningsForModules;